import {
  OutcomeDocument,
  OutcomeReadyResponse,
  OutcomeReadySubmission,
  OutcomeStandaloneDocument,
} from '../types';

import { OUTCOME_READY_MOCK } from './outcome-ready.mock';

/** Documents returned per mock page of the Outcomes Ready payload. */
export const OUTCOME_READY_MOCK_PAGE_SIZE = 25;

/**
 * A grouped document paired with its owning submission, used while flattening
 * the payload into a single pageable stream.
 */
type GroupedStreamEntry = {
  owner: OutcomeReadySubmission;
  document: OutcomeDocument;
};

/**
 * Slices the Outcomes Ready mock into fixed-size document pages, simulating a
 * paginated back end before one exists.
 *
 * The pagination unit is the **document**. Grouped documents stream in
 * submission order, and standalone (individual) documents are interleaved at
 * evenly spaced positions — slot `k` of `count` sits at stream index
 * `floor((k + 1) × streamLength / (count + 1))` — so every page mixes both
 * kinds instead of bunching the individual documents at one end. Each page is
 * exactly `pageSize` documents until the final partial page.
 *
 * A submission group whose documents straddle a page boundary is split — the
 * page carries only its in-range documents, while the group's `document_count`
 * keeps the group's full size so headers can badge the true count before every
 * page has loaded.
 *
 * Top-level `document_count` and `submission_count` always mimic the full
 * dataset totals, never the page slice. A page beyond the data returns empty
 * `submissions` / `standalone_documents` with the totals intact; a page below
 * 1 is clamped to the first page.
 *
 * @param page - 1-based page number to slice (defaults to the first page).
 * @param pageSize - Documents per page (defaults to {@link OUTCOME_READY_MOCK_PAGE_SIZE}).
 * @param source - Payload to paginate (defaults to `OUTCOME_READY_MOCK`; injectable for tests).
 * @returns The page's slice of the payload with full-dataset totals.
 *
 * @example
 * ```ts
 * paginateOutcomeReadyMock();      // docs 1–25 (24 grouped + 1 individual), document_count = 109
 * paginateOutcomeReadyMock(2);     // docs 26–50, again mixing grouped + individual
 * paginateOutcomeReadyMock(99);    // { submissions: [], standalone_documents: [], …totals }
 * ```
 */
export function paginateOutcomeReadyMock(
  page = 1,
  pageSize = OUTCOME_READY_MOCK_PAGE_SIZE,
  source: OutcomeReadyResponse = OUTCOME_READY_MOCK,
): OutcomeReadyResponse {
  const _page = Math.max(1, Math.trunc(page));
  const _start = (_page - 1) * pageSize;
  const _end = _start + pageSize;

  // Flatten grouped documents, keeping each document's owning submission.
  const _groupedEntries: GroupedStreamEntry[] = [];
  for (const _submission of source.submissions) {
    for (const _document of _submission.documents) {
      _groupedEntries.push({ owner: _submission, document: _document });
    }
  }

  const _standaloneCount = source.standalone_documents.length;
  const _streamLength = _groupedEntries.length + _standaloneCount;

  // Walk the interleaved stream once, collecting only in-range documents.
  const _submissions: OutcomeReadySubmission[] = [];
  const _standaloneDocuments: OutcomeStandaloneDocument[] = [];
  let _groupedCursor = 0;
  let _standaloneCursor = 0;

  for (let _index = 0; _index < _streamLength; _index++) {
    const _nextStandaloneSlot =
      _standaloneCursor < _standaloneCount
        ? Math.floor(
            ((_standaloneCursor + 1) * _streamLength) / (_standaloneCount + 1),
          )
        : -1;
    const _isStandaloneTurn =
      _index === _nextStandaloneSlot ||
      _groupedCursor >= _groupedEntries.length;
    const _isInRange = _index >= _start && _index < _end;

    if (_isStandaloneTurn) {
      const _document = source.standalone_documents[_standaloneCursor];
      _standaloneCursor += 1;
      if (_isInRange) {
        _standaloneDocuments.push(_document);
      }
      continue;
    }

    const _entry = _groupedEntries[_groupedCursor];
    _groupedCursor += 1;
    if (_isInRange) {
      const _lastSubmission = _submissions[_submissions.length - 1];
      if (_lastSubmission?.workspace_id === _entry.owner.workspace_id) {
        _lastSubmission.documents.push(_entry.document);
      } else {
        _submissions.push({ ..._entry.owner, documents: [_entry.document] });
      }
    }
  }

  return {
    document_count: source.document_count,
    submission_count: source.submission_count,
    submissions: _submissions,
    standalone_documents: _standaloneDocuments,
  };
}
